import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Snackbar,
  Tabs,
  Tab,
  InputAdornment,
  ClickAwayListener,
} from '@mui/material';
import {
  Add as AddIcon,
  Call as CallIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Phone as PhoneIcon,
  Upload as UploadIcon,
  FileUpload as FileUploadIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useSnackbar } from 'notistack';
import Navbar from './Navbar';
import config from '../config';
import { API_URL } from '../config';
import { v4 as uuidv4 } from 'uuid';

const Leads = () => {
  const { user } = useAuth();
  const [leadsTables, setLeadsTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [leads, setLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  
  // Dialogs
  const [createTableDialog, setCreateTableDialog] = useState(false);
  const [uploadLeadsDialog, setUploadLeadsDialog] = useState(false);
  const [addLeadDialog, setAddLeadDialog] = useState(false);
  const [callLeadDialog, setCallLeadDialog] = useState(false);
  const [callMultipleDialog, setCallMultipleDialog] = useState(false);
  const [columnMappingDialog, setColumnMappingDialog] = useState(false);
  const [createClientDialog, setCreateClientDialog] = useState(false);
  
  // Form states
  const [newTableName, setNewTableName] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [callStatus, setCallStatus] = useState(null);
  const [fileColumns, setFileColumns] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [tempFile, setTempFile] = useState(null);
  const [newLead, setNewLead] = useState({
    phone_number: '',
    email: '',
    first_name: '',
    last_name: ''
  });
  const [previewData, setPreviewData] = useState([]);
  const [previewStep, setPreviewStep] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    email: '',
  });
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Set up state for editing
  const [editingCell, setEditingCell] = useState({ 
    leadId: null,
    field: null, 
    value: '',
    initialValue: '' 
  });

  // Phone country code state for the formatter
  const [phoneCountry, setPhoneCountry] = useState('us');

  // Helper function to get country options with flags
  const getCountryOptions = () => [
    { code: "af", name: "Afghanistan", dial_code: "+93" },
    { code: "al", name: "Albania", dial_code: "+355" },
    { code: "dz", name: "Algeria", dial_code: "+213" },
    { code: "as", name: "AmericanSamoa", dial_code: "+1684" },
    { code: "ad", name: "Andorra", dial_code: "+376" },
    { code: "ao", name: "Angola", dial_code: "+244" },
    { code: "ai", name: "Anguilla", dial_code: "+1264" },
    { code: "ag", name: "Antigua and Barbuda", dial_code: "+1268" },
    { code: "ar", name: "Argentina", dial_code: "+54" },
    { code: "am", name: "Armenia", dial_code: "+374" },
    { code: "aw", name: "Aruba", dial_code: "+297" },
    { code: "au", name: "Australia", dial_code: "+61" },
    { code: "at", name: "Austria", dial_code: "+43" },
    { code: "az", name: "Azerbaijan", dial_code: "+994" },
    { code: "bs", name: "Bahamas", dial_code: "+1242" },
    { code: "bh", name: "Bahrain", dial_code: "+973" },
    { code: "bd", name: "Bangladesh", dial_code: "+880" },
    { code: "bb", name: "Barbados", dial_code: "+1246" },
    { code: "by", name: "Belarus", dial_code: "+375" },
    { code: "be", name: "Belgium", dial_code: "+32" },
    { code: "bz", name: "Belize", dial_code: "+501" },
    { code: "bj", name: "Benin", dial_code: "+229" },
    { code: "bm", name: "Bermuda", dial_code: "+1441" },
    { code: "bt", name: "Bhutan", dial_code: "+975" },
    { code: "bo", name: "Bolivia", dial_code: "+591" },
    { code: "ba", name: "Bosnia and Herzegovina", dial_code: "+387" },
    { code: "bw", name: "Botswana", dial_code: "+267" },
    { code: "br", name: "Brazil", dial_code: "+55" },
    { code: "io", name: "British Indian Ocean Territory", dial_code: "+246" },
    { code: "vg", name: "British Virgin Islands", dial_code: "+1284" },
    { code: "bn", name: "Brunei", dial_code: "+673" },
    { code: "bg", name: "Bulgaria", dial_code: "+359" },
    { code: "bf", name: "Burkina Faso", dial_code: "+226" },
    { code: "bi", name: "Burundi", dial_code: "+257" },
    { code: "kh", name: "Cambodia", dial_code: "+855" },
    { code: "cm", name: "Cameroon", dial_code: "+237" },
    { code: "ca", name: "Canada", dial_code: "+1" },
    { code: "cv", name: "Cape Verde", dial_code: "+238" },
    { code: "ky", name: "Cayman Islands", dial_code: "+1345" },
    { code: "cf", name: "Central African Republic", dial_code: "+236" },
    { code: "td", name: "Chad", dial_code: "+235" },
    { code: "cl", name: "Chile", dial_code: "+56" },
    { code: "cn", name: "China", dial_code: "+86" },
    { code: "co", name: "Colombia", dial_code: "+57" },
    { code: "km", name: "Comoros", dial_code: "+269" },
    { code: "cd", name: "Congo (DRC)", dial_code: "+243" },
    { code: "cg", name: "Congo (Republic)", dial_code: "+242" },
    { code: "ck", name: "Cook Islands", dial_code: "+682" },
    { code: "cr", name: "Costa Rica", dial_code: "+506" },
    { code: "hr", name: "Croatia", dial_code: "+385" },
    { code: "cu", name: "Cuba", dial_code: "+53" },
    { code: "cy", name: "Cyprus", dial_code: "+357" },
    { code: "cz", name: "Czech Republic", dial_code: "+420" },
    { code: "dk", name: "Denmark", dial_code: "+45" },
    { code: "dj", name: "Djibouti", dial_code: "+253" },
    { code: "dm", name: "Dominica", dial_code: "+1767" },
    { code: "do", name: "Dominican Republic", dial_code: "+1" },
    { code: "ec", name: "Ecuador", dial_code: "+593" },
    { code: "eg", name: "Egypt", dial_code: "+20" },
    { code: "sv", name: "El Salvador", dial_code: "+503" },
    { code: "gq", name: "Equatorial Guinea", dial_code: "+240" },
    { code: "er", name: "Eritrea", dial_code: "+291" },
    { code: "ee", name: "Estonia", dial_code: "+372" },
    { code: "et", name: "Ethiopia", dial_code: "+251" },
    { code: "fk", name: "Falkland Islands", dial_code: "+500" },
    { code: "fo", name: "Faroe Islands", dial_code: "+298" },
    { code: "fj", name: "Fiji", dial_code: "+679" },
    { code: "fi", name: "Finland", dial_code: "+358" },
    { code: "fr", name: "France", dial_code: "+33" },
    { code: "gf", name: "French Guiana", dial_code: "+594" },
    { code: "pf", name: "French Polynesia", dial_code: "+689" },
    { code: "ga", name: "Gabon", dial_code: "+241" },
    { code: "gm", name: "Gambia", dial_code: "+220" },
    { code: "ge", name: "Georgia", dial_code: "+995" },
    { code: "de", name: "Germany", dial_code: "+49" },
    { code: "gh", name: "Ghana", dial_code: "+233" },
    { code: "gi", name: "Gibraltar", dial_code: "+350" },
    { code: "gr", name: "Greece", dial_code: "+30" },
    { code: "gl", name: "Greenland", dial_code: "+299" },
    { code: "gd", name: "Grenada", dial_code: "+1473" },
    { code: "gp", name: "Guadeloupe", dial_code: "+590" },
    { code: "gu", name: "Guam", dial_code: "+1671" },
    { code: "gt", name: "Guatemala", dial_code: "+502" },
    { code: "gn", name: "Guinea", dial_code: "+224" },
    { code: "gw", name: "Guinea-Bissau", dial_code: "+245" },
    { code: "gy", name: "Guyana", dial_code: "+592" },
    { code: "ht", name: "Haiti", dial_code: "+509" },
    { code: "hn", name: "Honduras", dial_code: "+504" },
    { code: "hk", name: "Hong Kong", dial_code: "+852" },
    { code: "hu", name: "Hungary", dial_code: "+36" },
    { code: "is", name: "Iceland", dial_code: "+354" },
    { code: "in", name: "India", dial_code: "+91" },
    { code: "id", name: "Indonesia", dial_code: "+62" },
    { code: "ir", name: "Iran", dial_code: "+98" },
    { code: "iq", name: "Iraq", dial_code: "+964" },
    { code: "ie", name: "Ireland", dial_code: "+353" },
    { code: "il", name: "Israel", dial_code: "+972" },
    { code: "it", name: "Italy", dial_code: "+39" },
    { code: "ci", name: "Ivory Coast", dial_code: "+225" },
    { code: "jm", name: "Jamaica", dial_code: "+1876" },
    { code: "jp", name: "Japan", dial_code: "+81" },
    { code: "jo", name: "Jordan", dial_code: "+962" },
    { code: "kz", name: "Kazakhstan", dial_code: "+7" },
    { code: "ke", name: "Kenya", dial_code: "+254" },
    { code: "ki", name: "Kiribati", dial_code: "+686" },
    { code: "kw", name: "Kuwait", dial_code: "+965" },
    { code: "kg", name: "Kyrgyzstan", dial_code: "+996" },
    { code: "la", name: "Laos", dial_code: "+856" },
    { code: "lv", name: "Latvia", dial_code: "+371" },
    { code: "lb", name: "Lebanon", dial_code: "+961" },
    { code: "ls", name: "Lesotho", dial_code: "+266" },
    { code: "lr", name: "Liberia", dial_code: "+231" },
    { code: "ly", name: "Libya", dial_code: "+218" },
    { code: "li", name: "Liechtenstein", dial_code: "+423" },
    { code: "lt", name: "Lithuania", dial_code: "+370" },
    { code: "lu", name: "Luxembourg", dial_code: "+352" },
    { code: "mo", name: "Macau", dial_code: "+853" },
    { code: "mk", name: "Macedonia", dial_code: "+389" },
    { code: "mg", name: "Madagascar", dial_code: "+261" },
    { code: "mw", name: "Malawi", dial_code: "+265" },
    { code: "my", name: "Malaysia", dial_code: "+60" },
    { code: "mv", name: "Maldives", dial_code: "+960" },
    { code: "ml", name: "Mali", dial_code: "+223" },
    { code: "mt", name: "Malta", dial_code: "+356" },
    { code: "mh", name: "Marshall Islands", dial_code: "+692" },
    { code: "mq", name: "Martinique", dial_code: "+596" },
    { code: "mr", name: "Mauritania", dial_code: "+222" },
    { code: "mu", name: "Mauritius", dial_code: "+230" },
    { code: "mx", name: "Mexico", dial_code: "+52" },
    { code: "fm", name: "Micronesia", dial_code: "+691" },
    { code: "md", name: "Moldova", dial_code: "+373" },
    { code: "mc", name: "Monaco", dial_code: "+377" },
    { code: "mn", name: "Mongolia", dial_code: "+976" },
    { code: "me", name: "Montenegro", dial_code: "+382" },
    { code: "ms", name: "Montserrat", dial_code: "+1664" },
    { code: "ma", name: "Morocco", dial_code: "+212" },
    { code: "mz", name: "Mozambique", dial_code: "+258" },
    { code: "mm", name: "Myanmar", dial_code: "+95" },
    { code: "na", name: "Namibia", dial_code: "+264" },
    { code: "nr", name: "Nauru", dial_code: "+674" },
    { code: "np", name: "Nepal", dial_code: "+977" },
    { code: "nl", name: "Netherlands", dial_code: "+31" },
    { code: "nc", name: "New Caledonia", dial_code: "+687" },
    { code: "nz", name: "New Zealand", dial_code: "+64" },
    { code: "ni", name: "Nicaragua", dial_code: "+505" },
    { code: "ne", name: "Niger", dial_code: "+227" },
    { code: "ng", name: "Nigeria", dial_code: "+234" },
    { code: "nu", name: "Niue", dial_code: "+683" },
    { code: "kp", name: "North Korea", dial_code: "+850" },
    { code: "no", name: "Norway", dial_code: "+47" },
    { code: "om", name: "Oman", dial_code: "+968" },
    { code: "pk", name: "Pakistan", dial_code: "+92" },
    { code: "pw", name: "Palau", dial_code: "+680" },
    { code: "ps", name: "Palestine", dial_code: "+970" },
    { code: "pa", name: "Panama", dial_code: "+507" },
    { code: "pg", name: "Papua New Guinea", dial_code: "+675" },
    { code: "py", name: "Paraguay", dial_code: "+595" },
    { code: "pe", name: "Peru", dial_code: "+51" },
    { code: "ph", name: "Philippines", dial_code: "+63" },
    { code: "pl", name: "Poland", dial_code: "+48" },
    { code: "pt", name: "Portugal", dial_code: "+351" },
    { code: "pr", name: "Puerto Rico", dial_code: "+1" },
    { code: "qa", name: "Qatar", dial_code: "+974" },
    { code: "re", name: "Réunion", dial_code: "+262" },
    { code: "ro", name: "Romania", dial_code: "+40" },
    { code: "ru", name: "Russia", dial_code: "+7" },
    { code: "rw", name: "Rwanda", dial_code: "+250" },
    { code: "sh", name: "Saint Helena", dial_code: "+290" },
    { code: "kn", name: "Saint Kitts and Nevis", dial_code: "+1869" },
    { code: "lc", name: "Saint Lucia", dial_code: "+1758" },
    { code: "pm", name: "Saint Pierre and Miquelon", dial_code: "+508" },
    { code: "vc", name: "Saint Vincent and the Grenadines", dial_code: "+1784" },
    { code: "ws", name: "Samoa", dial_code: "+685" },
    { code: "sm", name: "San Marino", dial_code: "+378" },
    { code: "st", name: "São Tomé and Príncipe", dial_code: "+239" },
    { code: "sa", name: "Saudi Arabia", dial_code: "+966" },
    { code: "sn", name: "Senegal", dial_code: "+221" },
    { code: "rs", name: "Serbia", dial_code: "+381" },
    { code: "sc", name: "Seychelles", dial_code: "+248" },
    { code: "sl", name: "Sierra Leone", dial_code: "+232" },
    { code: "sg", name: "Singapore", dial_code: "+65" },
    { code: "sk", name: "Slovakia", dial_code: "+421" },
    { code: "si", name: "Slovenia", dial_code: "+386" },
    { code: "sb", name: "Solomon Islands", dial_code: "+677" },
    { code: "so", name: "Somalia", dial_code: "+252" },
    { code: "za", name: "South Africa", dial_code: "+27" },
    { code: "kr", name: "South Korea", dial_code: "+82" },
    { code: "ss", name: "South Sudan", dial_code: "+211" },
    { code: "es", name: "Spain", dial_code: "+34" },
    { code: "lk", name: "Sri Lanka", dial_code: "+94" },
    { code: "sd", name: "Sudan", dial_code: "+249" },
    { code: "sr", name: "Suriname", dial_code: "+597" },
    { code: "sz", name: "Swaziland", dial_code: "+268" },
    { code: "se", name: "Sweden", dial_code: "+46" },
    { code: "ch", name: "Switzerland", dial_code: "+41" },
    { code: "sy", name: "Syria", dial_code: "+963" },
    { code: "tw", name: "Taiwan", dial_code: "+886" },
    { code: "tj", name: "Tajikistan", dial_code: "+992" },
    { code: "tz", name: "Tanzania", dial_code: "+255" },
    { code: "th", name: "Thailand", dial_code: "+66" },
    { code: "tl", name: "Timor-Leste", dial_code: "+670" },
    { code: "tg", name: "Togo", dial_code: "+228" },
    { code: "tk", name: "Tokelau", dial_code: "+690" },
    { code: "to", name: "Tonga", dial_code: "+676" },
    { code: "tt", name: "Trinidad and Tobago", dial_code: "+1868" },
    { code: "tn", name: "Tunisia", dial_code: "+216" },
    { code: "tr", name: "Turkey", dial_code: "+90" },
    { code: "tm", name: "Turkmenistan", dial_code: "+993" },
    { code: "tc", name: "Turks and Caicos Islands", dial_code: "+1649" },
    { code: "tv", name: "Tuvalu", dial_code: "+688" },
    { code: "ug", name: "Uganda", dial_code: "+256" },
    { code: "ua", name: "Ukraine", dial_code: "+380" },
    { code: "ae", name: "United Arab Emirates", dial_code: "+971" },
    { code: "gb", name: "United Kingdom", dial_code: "+44" },
    { code: "us", name: "United States", dial_code: "+1" },
    { code: "uy", name: "Uruguay", dial_code: "+598" },
    { code: "uz", name: "Uzbekistan", dial_code: "+998" },
    { code: "vu", name: "Vanuatu", dial_code: "+678" },
    { code: "va", name: "Vatican City", dial_code: "+39" },
    { code: "ve", name: "Venezuela", dial_code: "+58" },
    { code: "vn", name: "Vietnam", dial_code: "+84" },
    { code: "vi", name: "Virgin Islands, U.S.", dial_code: "+1340" },
    { code: "ye", name: "Yemen", dial_code: "+967" },
    { code: "zm", name: "Zambia", dial_code: "+260" },
    { code: "zw", name: "Zimbabwe", dial_code: "+263" }
  ];

  // Enhanced country selector component with search and flags
  const CountrySelector = ({ value, onChange, ...props }) => {
    const countries = getCountryOptions();
    const [searchText, setSearchText] = useState('');
    const [dropdownOpen, setDropdownOpen] = useState(false);

    // Find the current selected country
    const selectedCountry = countries.find(c => c.code === value) || countries.find(c => c.code === 'us');

    // Filter countries based on search text
    const filteredCountries = searchText 
      ? countries.filter(c => 
          c.name.toLowerCase().includes(searchText.toLowerCase()) || 
          c.dial_code.includes(searchText)
        )
      : countries;

    return (
      <Box sx={{ position: 'relative', ...props.sx }}>
        <Box 
          onClick={() => setDropdownOpen(!dropdownOpen)} 
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            border: '1px solid rgba(0, 0, 0, 0.23)',
            borderRadius: '4px',
            p: 1, 
            cursor: 'pointer',
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
            height: props.small ? '40px' : '56px',
          }}
        >
          <Box component="img" 
            src={`https://flagcdn.com/w20/${selectedCountry?.code.toLowerCase()}.png`} 
            alt={selectedCountry?.name}
            sx={{ mr: 1, width: 20, height: 'auto' }}
          />
          <Typography variant={props.small ? "body2" : "body1"}>
            {selectedCountry?.dial_code}
          </Typography>
        </Box>

        {dropdownOpen && (
          <ClickAwayListener onClickAway={() => setDropdownOpen(false)}>
            <Box sx={{
              position: 'absolute',
              top: props.small ? '45px' : '60px',
              left: 0,
              zIndex: 10,
              width: '280px',
              maxHeight: '300px',
              overflowY: 'auto',
              bgcolor: 'background.paper',
              boxShadow: 3,
              borderRadius: 1,
              p: 1
            }}>
              <TextField
                size="small"
                placeholder="Search countries..."
                fullWidth
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{ mb: 1 }}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FlagIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Grid container spacing={1}>
                {filteredCountries.map(country => (
                  <Grid item xs={6} key={country.code}>
                    <Button
                      fullWidth
                      onClick={() => onChange(country.code)}
                      startIcon={<Box component="img" src={`https://flagcdn.com/w20/${country.code.toLowerCase()}.png`} alt={country.name} sx={{ mr: 1 }} />}
                      sx={{ justifyContent: 'flex-start' }}
                    >
                      {country.name}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </ClickAwayListener>
        )}
      </Box>
    );
  };

  const handleEditClick = (leadId, field, value) => {
    setEditingCell({ 
      leadId, 
      field, 
      value: value || '', 
      initialValue: value || '' 
    });
  };

  const handleEditCancel = () => {
    setEditingCell({ 
      leadId: null, 
      field: null, 
      value: '', 
      initialValue: '' 
    });
  };

  const handleEditChange = (e) => {
    setEditingCell({
      ...editingCell,
      value: e.target.value
    });
  };
  
  // Add a new state variable to track individual number edits
  const [phoneNumberOverrides, setPhoneNumberOverrides] = useState({});
  const [reviewNumbersMode, setReviewNumbersMode] = useState(false);

  // Load leads tables on component mount
  useEffect(() => {
    fetchLeadsTables();
    fetchAgents();
  }, []);

  // Fetch available outbound agents
  const fetchAgents = async () => {
    try {
      console.log('Fetching outbound agents...');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/agents?type=outbound`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Agents fetched successfully:', data);
        
        // Handle different response formats
        let agentsList = [];
        if (Array.isArray(data)) {
          agentsList = data;
        } else if (data.agents && Array.isArray(data.agents)) {
          agentsList = data.agents;
      } else {
          console.warn('Unexpected agent data format:', data);
          agentsList = [];
        }
        
        // Filter for outbound agents if needed
        const outboundAgents = agentsList.filter(agent => 
          agent.agent_type === 'outbound' || 
          agent.type === 'outbound'
        );
        
        console.log('Filtered outbound agents:', outboundAgents);
        setAgents(outboundAgents);
      } else {
        console.error('Failed to fetch agents:', response.status, await response.text());
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  // Fetch all leads tables
  const fetchLeadsTables = async () => {
    setTableLoading(true);
    try {
      console.log('Fetching leads tables...');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/leads-tables/tables`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Leads tables fetched successfully:', data);
        setLeadsTables(data || []);
      } else {
        console.error('Failed to fetch leads tables:', response.status, await response.text());
        showSnackbar('Failed to fetch leads tables', 'error');
      }
    } catch (error) {
      console.error('Error fetching leads tables:', error);
      showSnackbar('Error loading leads tables', 'error');
    } finally {
      setTableLoading(false);
    }
  };

  // Fetch leads from a specific table
  const fetchLeadsFromTable = async (tableId) => {
    setLoading(true);
    setSelectedLeads([]);
    try {
      console.log(`Fetching leads from table ${tableId}...`);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/leads-tables/tables/${tableId}/leads`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log(`Leads fetched successfully from table ${tableId}:`, data);
        setLeads(data.leads || []);
        
        // Find and set the selected table
        const table = leadsTables.find(t => t.id === tableId);
        setSelectedTable(table || null);
      } else {
        console.error(`Failed to fetch leads from table ${tableId}:`, response.status, await response.text());
        showSnackbar('Failed to fetch leads', 'error');
      }
    } catch (error) {
      console.error(`Error fetching leads from table ${tableId}:`, error);
      showSnackbar('Error loading leads', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Create a new leads table
  const handleCreateTable = async () => {
    if (!newTableName.trim()) {
      showSnackbar('Table name is required', 'error');
      return;
    }

    try {
      console.log(`Creating new table: ${newTableName}...`);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/leads-tables/tables`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newTableName.trim(),
          columns: ['phone_number', 'email', 'first_name', 'last_name']
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Table created successfully:', data);
        setLeadsTables([...leadsTables, data.table]);
        setCreateTableDialog(false);
        setNewTableName('');
        showSnackbar('Leads table created successfully', 'success');
      } else {
        console.error('Failed to create leads table:', response.status, await response.text());
        showSnackbar('Failed to create leads table', 'error');
      }
    } catch (error) {
      console.error('Error creating leads table:', error);
      showSnackbar('Error creating leads table', 'error');
    }
  };

  // Upload leads to a table
  const handleUploadLeads = async () => {
    if (!csvFile || !selectedTable) {
      showSnackbar('Please select a file and a table', 'error');
      return;
    }

    const formData = new FormData();
    formData.append('file', csvFile);

    try {
      console.log(`Uploading leads to table ${selectedTable.id}...`);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/leads-tables/tables/${selectedTable.id}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Leads uploaded successfully:', data);
        setUploadLeadsDialog(false);
        setCsvFile(null);
        fetchLeadsFromTable(selectedTable.id);
        showSnackbar('Leads uploaded successfully', 'success');
      } else {
        console.error('Failed to upload leads:', response.status, await response.text());
        showSnackbar('Failed to upload leads', 'error');
      }
    } catch (error) {
      console.error('Error uploading leads:', error);
      showSnackbar('Error uploading leads', 'error');
    }
  };

  // Call a single lead
  const handleCallLead = async (lead) => {
    if (!selectedAgent) {
      showSnackbar('Please select an agent', 'error');
      return;
    }

    setCallStatus('calling');
    
    try {
      console.log(`Calling lead ${lead.id} (${lead.phone_number}) with agent ${selectedAgent}...`);
      const token = localStorage.getItem('token');
      const roomName = `outbound-${uuidv4().substring(0, 8)}`;
      
      const response = await fetch(`${API_URL}/api/outbound/dispatch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          room: roomName,
          agent_name: selectedAgent,
          metadata: {
            phone_number: lead.phone_number,
            lead_id: lead.id,
            lead_name: lead.first_name ? `${lead.first_name} ${lead.last_name || ''}` : 'Unknown'
          }
        })
      });

      const responseText = await response.text();
      console.log(`Call response: ${response.status}`, responseText);

      if (response.ok) {
        const data = responseText ? JSON.parse(responseText) : {};
        setCallStatus('success');
        console.log('Call initiated successfully:', data);
        showSnackbar(`Call to ${lead.phone_number} initiated successfully`, 'success');
        setCallLeadDialog(false);
        
        // Update the lead's call count
        const updatedLeads = leads.map(l => {
          if (l.id === lead.id) {
            return { ...l, call_count: (l.call_count || 0) + 1 };
          }
          return l;
        });
        setLeads(updatedLeads);
        
        // Also update the call count on the server
        try {
          const updateResponse = await fetch(`${API_URL}/api/leads-tables/tables/${selectedTable.id}/leads/${lead.id}/last-called`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          if (updateResponse.ok) {
            console.log('Lead call count updated on server');
          }
        } catch (updateError) {
          console.error('Error updating lead call count:', updateError);
        }
      } else {
        setCallStatus('error');
        console.error('Failed to initiate call:', response.status, responseText);
        showSnackbar('Failed to initiate call', 'error');
      }
    } catch (error) {
      console.error('Error calling lead:', error);
      setCallStatus('error');
      showSnackbar('Error initiating call', 'error');
    }
  };

  // Call multiple leads
  const handleCallMultipleLeads = async () => {
    if (!selectedAgent || selectedLeads.length === 0) {
      showSnackbar('Please select an agent and at least one lead', 'error');
      return;
    }

    setCallStatus('calling');
    console.log(`Calling ${selectedLeads.length} leads with agent ${selectedAgent}...`);
    let successCount = 0;
    let errorCount = 0;

    for (const leadId of selectedLeads) {
      const lead = leads.find(l => l.id === leadId);
      if (!lead) {
        console.warn(`Lead with ID ${leadId} not found in current leads array.`);
        continue;
      }

      try {
        console.log(`Calling lead ${lead.id} (${lead.phone_number})...`);
        const token = localStorage.getItem('token');
        const roomName = `outbound-${uuidv4().substring(0, 8)}`;
        
        const response = await fetch(`${API_URL}/api/outbound/dispatch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            room: roomName,
            agent_name: selectedAgent,
            metadata: {
              phone_number: lead.phone_number,
              lead_id: lead.id,
              lead_name: lead.first_name ? `${lead.first_name} ${lead.last_name || ''}` : 'Unknown'
            }
          })
        });

        const responseText = await response.text();

        if (response.ok) {
          console.log(`Successfully called lead ${lead.id}:`, responseText);
          successCount++;
          
          // Update the lead's call count
          const updatedLeads = leads.map(l => {
            if (l.id === lead.id) {
              return { ...l, call_count: (l.call_count || 0) + 1 };
            }
            return l;
          });
          setLeads(updatedLeads);
          
          // Also update the call count on the server
          try {
            const updateResponse = await fetch(`${API_URL}/api/leads-tables/tables/${selectedTable.id}/leads/${lead.id}/last-called`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            if (updateResponse.ok) {
              console.log(`Lead ${lead.id} call count updated on server`);
            }
          } catch (updateError) {
            console.error(`Error updating lead ${lead.id} call count:`, updateError);
          }
        } else {
          console.error(`Failed to call lead ${lead.id}:`, response.status, responseText);
          errorCount++;
        }
      } catch (error) {
        console.error(`Error calling lead ${lead.id}:`, error);
        errorCount++;
      }
      
      // Wait a bit between calls to avoid overwhelming the system
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    console.log(`Batch calling complete. Success: ${successCount}, Failed: ${errorCount}`);
    setCallStatus(errorCount === 0 ? 'success' : 'partial');
    showSnackbar(`Called ${successCount} leads successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}`, errorCount > 0 ? 'warning' : 'success');
    setCallMultipleDialog(false);
    setSelectedLeads([]);
  };

  // Toggle selection of a lead
  const toggleLeadSelection = (leadId) => {
    setSelectedLeads(prev => {
      if (prev.includes(leadId)) {
        return prev.filter(id => id !== leadId);
      } else {
        return [...prev, leadId];
      }
    });
  };

  // Select or deselect all leads
  const toggleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map(lead => lead.id));
    }
  };

  // Show snackbar message
  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Handle snackbar close
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Handle delete for multiple leads
  const handleBatchDelete = async () => {
    if (selectedLeads.length === 0) {
      showSnackbar('Please select at least one lead to delete', 'warning');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedLeads.length} selected leads?`)) {
      return;
    }

    try {
      console.log(`Deleting ${selectedLeads.length} leads from table ${selectedTable.id}...`);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/leads-tables/tables/${selectedTable.id}/batch-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          lead_ids: selectedLeads
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Batch delete response:', data);
        
        // Remove the deleted leads from the state
        setLeads(leads.filter(lead => !selectedLeads.includes(lead.id)));
        setSelectedLeads([]);
        showSnackbar(`Successfully deleted ${data.deleted_count} leads`, 'success');
      } else {
        console.error('Failed to batch delete leads:', response.status, await response.text());
        showSnackbar('Failed to delete selected leads', 'error');
      }
    } catch (error) {
      console.error('Error batch deleting leads:', error);
      showSnackbar('Error deleting leads', 'error');
    }
  };

  // Handle file select - enhance for Excel files
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setTempFile(file);
    setCsvFile(file);
    setPreviewData([]);
    setPreviewStep(false);
    
    try {
      // For CSV files, extract headers and preview data
      if (file.name.toLowerCase().endsWith('.csv')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target.result;
          const lines = content.split('\n');
          const firstLine = lines[0];
          let headers = firstLine.split(',').map(h => h.trim().replace(/["']/g, ''));
          
          // Set file columns
          setFileColumns(headers);
          
          // Initialize mapping with matching column names
          const initialMapping = {};
          headers.forEach(header => {
            const lowerHeader = header.toLowerCase();
            if (lowerHeader.includes('phone')) {
              initialMapping['phone_number'] = header;
            } else if (lowerHeader.includes('email')) {
              initialMapping['email'] = header;
            } else if ((lowerHeader.includes('first') && lowerHeader.includes('name')) || 
                      lowerHeader === 'firstname' || lowerHeader === 'fname') {
              initialMapping['first_name'] = header;
            } else if ((lowerHeader.includes('last') && lowerHeader.includes('name')) || 
                      lowerHeader === 'lastname' || lowerHeader === 'lname') {
              initialMapping['last_name'] = header;
            }
          });
          
          console.log("CSV Headers:", headers);
          console.log("Suggested mapping:", initialMapping);
          
          // Generate preview data (first 5 rows)
          const previewRows = [];
          for (let i = 1; i < Math.min(6, lines.length); i++) {
            if (lines[i].trim()) {
              const values = lines[i].split(',').map(v => v.trim().replace(/["']/g, ''));
              const row = {};
              headers.forEach((header, index) => {
                if (index < values.length) {
                  row[header] = values[index];
                } else {
                  row[header] = '';
                }
              });
              previewRows.push(row);
            }
          }
          
          setPreviewData(previewRows);
          setColumnMapping(initialMapping);
          setColumnMappingDialog(true);
        };
        reader.readAsText(file);
      } 
      // For Excel files, extract headers using SheetJS
      else if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) {
        const reader = new FileReader();
        reader.onload = async (e) => {
          const data = new Uint8Array(e.target.result);
          
          // Load SheetJS from CDN if not available
          if (!window.XLSX) {
            await loadSheetJS();
          }
          
          try {
            const workbook = window.XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            
            // Convert to JSON to get headers and data
            const jsonData = window.XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            
            if (jsonData.length > 0) {
              const headers = jsonData[0];
              
              // Set file columns
              setFileColumns(headers);
              
              // Initialize mapping with matching column names
              const initialMapping = {};
              headers.forEach(header => {
                if (typeof header === 'string') {
                  const lowerHeader = header.toLowerCase();
                  if (lowerHeader.includes('phone')) {
                    initialMapping['phone_number'] = header;
                  } else if (lowerHeader.includes('email')) {
                    initialMapping['email'] = header;
                  } else if ((lowerHeader.includes('first') && lowerHeader.includes('name')) || 
                            lowerHeader === 'firstname' || lowerHeader === 'fname') {
                    initialMapping['first_name'] = header;
                  } else if ((lowerHeader.includes('last') && lowerHeader.includes('name')) || 
                            lowerHeader === 'lastname' || lowerHeader === 'lname') {
                    initialMapping['last_name'] = header;
                  }
                }
              });
              
              console.log("Excel Headers:", headers);
              console.log("Suggested mapping:", initialMapping);
              
              // Generate preview data (first 5 rows)
              const previewRows = [];
              for (let i = 1; i < Math.min(6, jsonData.length); i++) {
                const row = {};
                headers.forEach((header, index) => {
                  row[header] = jsonData[i][index] || '';
                });
                previewRows.push(row);
              }
              
              setPreviewData(previewRows);
              setColumnMapping(initialMapping);
              setColumnMappingDialog(true);
            } else {
              showSnackbar('Excel file appears to be empty', 'error');
            }
          } catch (error) {
            console.error('Error parsing Excel file:', error);
            showSnackbar('Failed to parse Excel file', 'error');
          }
        };
        reader.readAsArrayBuffer(file);
      } else {
        showSnackbar('Unsupported file type. Please upload a CSV or Excel file.', 'error');
      }
    } catch (error) {
      console.error('Error processing file:', error);
      showSnackbar('Error processing file', 'error');
    }
  };

  // Load SheetJS from CDN
  const loadSheetJS = async () => {
    return new Promise((resolve, reject) => {
      if (window.XLSX) {
        resolve();
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load SheetJS library'));
      document.body.appendChild(script);
    });
  };

  // Handle cell edit start
  const handleEditStart = (lead, field) => {
    handleEditClick(lead.id, field, lead[field]);
  };

  // Handle edit save
  const handleEditSave = async () => {
    try {
      const token = localStorage.getItem('token');
      let valueToSave = editingCell.value;

      // If we're editing a phone number, format it properly
      if (editingCell.field === 'phone_number') {
        valueToSave = formatPhoneNumber(valueToSave, phoneCountry);
        console.log("Formatted phone for edit:", valueToSave);
      }

      const response = await fetch(`${API_URL}/api/leads-tables/tables/${selectedTable.id}/leads/${editingCell.leadId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          [editingCell.field]: valueToSave
        })
      });

      if (response.ok) {
        // Update the lead in the local state
        const updatedLeads = leads.map(lead => {
          if (lead.id === editingCell.leadId) {
            return { 
              ...lead, 
              [editingCell.field]: valueToSave 
            };
          }
          return lead;
        });
        setLeads(updatedLeads);
        showSnackbar(`Lead updated successfully`, 'success');
      } else {
        console.error('Failed to update lead:', response.status, await response.text());
        showSnackbar('Failed to update lead', 'error');
      }
    } catch (error) {
      console.error('Error updating lead:', error);
      showSnackbar('Error updating lead', 'error');
    } finally {
      handleEditCancel();
    }
  };

  // Create a more robust phone formatter that handles different country formats
  const formatPhoneNumber = (phoneNumber, country = 'us') => {
    if (!phoneNumber) return '';
    
    // Map of country codes
    const countryCodes = {
      'us': '1',
      'ca': '1',
      'gb': '44',
      'au': '61',
      'de': '49',
      'fr': '33',
      'it': '39',
      'es': '34',
      'pt': '351',
      'nl': '31',
      'be': '32',
      'ch': '41',
      'at': '43',
      'dk': '45',
      'se': '46',
      'no': '47',
      'fi': '358',
    };
    
    // Clean the phone number - remove all non-digit characters
    const cleanedNumber = phoneNumber.toString().replace(/\D/g, '');
    
    // If the number is empty after cleaning, return empty
    if (!cleanedNumber) return '';
    
    // Get the country code
    const countryCode = countryCodes[country] || '1';
    
    // If the number already starts with the country code, format accordingly
    if (cleanedNumber.startsWith(countryCode)) {
      return `+${cleanedNumber}`;
    }
    
    // If number starts with 0, remove it as we'll add the country code
    const numberWithoutLeadingZero = cleanedNumber.startsWith('0') 
      ? cleanedNumber.substring(1) 
      : cleanedNumber;
    
    // Return the formatted number with country code
    return `+${countryCode}${numberWithoutLeadingZero}`;
  };

  // Add a new lead with proper phone formatting
  const handleAddLead = async () => {
    if (!newLead.phone_number) {
      showSnackbar('Phone number is required', 'error');
      return;
    }

    try {
      console.log(`Adding new lead to table ${selectedTable.id}...`);
      const token = localStorage.getItem('token');
      
      // Format the phone number with the selected country code
      const formattedPhone = formatPhoneNumber(newLead.phone_number, phoneCountry);
      console.log("Formatted phone:", formattedPhone);
      
      const response = await fetch(`${API_URL}/api/leads-tables/tables/${selectedTable.id}/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          phone: formattedPhone,
          email: newLead.email,
          first_name: newLead.first_name,
          last_name: newLead.last_name
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Lead added successfully:', data);
        fetchLeadsFromTable(selectedTable.id);
        setAddLeadDialog(false);
        setNewLead({
          phone_number: '',
          email: '',
          first_name: '',
          last_name: ''
        });
        showSnackbar('Lead added successfully', 'success');
      } else {
        console.error('Failed to add lead:', response.status, await response.text());
        showSnackbar('Failed to add lead', 'error');
      }
    } catch (error) {
      console.error('Error adding lead:', error);
      showSnackbar('Error adding lead', 'error');
    }
  };

  // Add function to handle individual number overrides
  const handlePhoneNumberOverride = (rowIndex, originalValue, newValue) => {
    setPhoneNumberOverrides({
      ...phoneNumberOverrides,
      [rowIndex]: { 
        original: originalValue, 
        override: newValue,
        country: phoneCountry // Store the current country selection with the override
      }
    });
  };

  // Add function to reset overrides
  const resetOverrides = () => {
    setPhoneNumberOverrides({});
  };

  // Update the handleUploadLeadsWithMapping function to include overrides with proper formatting
  const handleUploadLeadsWithMapping = async () => {
    if (!tempFile || !selectedTable) {
      showSnackbar('Please select a file and a table', 'error');
      return;
    }

    const formData = new FormData();
    formData.append('file', tempFile);
    
    // Add column mapping to the request
    if (Object.keys(columnMapping).length > 0) {
      formData.append('column_map', JSON.stringify(columnMapping));
    }

    // Format phone number overrides with proper country codes if any exist
    if (Object.keys(phoneNumberOverrides).length > 0) {
      // Create a formatted version of the overrides with proper country code formatting
      const formattedOverrides = {};
      
      Object.entries(phoneNumberOverrides).forEach(([index, override]) => {
        const country = override.country || phoneCountry;
        const formattedNumber = formatPhoneNumber(override.override, country);
        
        formattedOverrides[index] = {
          original: override.original,
          override: formattedNumber
        };
      });
      
      formData.append('phone_overrides', JSON.stringify(formattedOverrides));
      console.log('Formatted phone overrides:', formattedOverrides);
    }

    // Add default country code
    formData.append('default_country_code', phoneCountry);

    try {
      console.log(`Uploading leads to table ${selectedTable.id} with column mapping:`, columnMapping);
      if (Object.keys(phoneNumberOverrides).length > 0) {
        console.log(`Including ${Object.keys(phoneNumberOverrides).length} phone number overrides with country code ${phoneCountry}`);
      }
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/leads-tables/tables/${selectedTable.id}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Leads uploaded successfully:', data);
        setUploadLeadsDialog(false);
        setColumnMappingDialog(false);
        setReviewNumbersMode(false);
        setTempFile(null);
        setCsvFile(null);
        setColumnMapping({});
        setPhoneNumberOverrides({});
        fetchLeadsFromTable(selectedTable.id);
        showSnackbar(`Successfully uploaded ${data.leads_added} leads with proper country codes`, 'success');
      } else {
        console.error('Failed to upload leads:', response.status, await response.text());
        showSnackbar('Failed to upload leads', 'error');
      }
    } catch (error) {
      console.error('Error uploading leads:', error);
      showSnackbar('Error uploading leads', 'error');
    }
  };

  // Add a new review step to the phone number formatting
  const proceedToReviewNumbers = () => {
    if (!columnMapping['phone_number']) {
      showSnackbar('You must map a column to Phone Number', 'error');
      return;
    }
    setReviewNumbersMode(true);
  };

  // Generate preview of what will be imported
  const generateImportPreview = () => {
    // Validate that we have a phone number mapping
    if (!columnMapping['phone_number']) {
      showSnackbar('You must map a column to Phone Number', 'error');
      return;
    }
    
    setPreviewStep(true);
  };

  // Create a new client
  const handleCreateClient = async () => {
    if (!newClient.name.trim() || !newClient.email.trim()) {
      showSnackbar('All fields are required', 'error');
      return;
    }

    try {
      console.log(`Creating new client: ${newClient.name}...`);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/organizations/clients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newClient.name.trim(),
          email: newClient.email.trim()
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Client created successfully:', data);
        setCreateClientDialog(false);
        setNewClient({
          name: '',
          email: '',
        });
        showSnackbar('Client created successfully', 'success');
      } else {
        console.error('Failed to create client:', response.status, await response.text());
        showSnackbar('Failed to create client', 'error');
      }
    } catch (error) {
      console.error('Error creating client:', error);
      showSnackbar('Error creating client', 'error');
    }
  };

  // Handle delete table
  const handleDeleteTable = async (tableId, tableName) => {
    if (!window.confirm(`Are you sure you want to delete the table "${tableName}" and all its leads? This action cannot be undone.`)) {
      return;
    }

    try {
      console.log(`Deleting table ${tableId}...`);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/leads-tables/tables/${tableId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        console.log('Table deleted successfully');
        setLeadsTables(leadsTables.filter(table => table.id !== tableId));
        if (selectedTable && selectedTable.id === tableId) {
          setSelectedTable(null);
          setLeads([]);
        }
        showSnackbar(`Table "${tableName}" deleted successfully`, 'success');
      } else {
        console.error('Failed to delete table:', response.status, await response.text());
        showSnackbar('Failed to delete table', 'error');
      }
    } catch (error) {
      console.error('Error deleting table:', error);
      showSnackbar('Error deleting table', 'error');
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ pt: 4, pb: 2 }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h4" component="h1" gutterBottom>
              Leads Management
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<BusinessIcon />}
              onClick={() => setCreateClientDialog(true)}
              sx={{ mr: 2 }}
            >
              Add Client
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setCreateTableDialog(true)}
            >
              New Table
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Tables List */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Leads Tables
          {tableLoading && <CircularProgress size={20} sx={{ ml: 2 }} />}
        </Typography>
        
        {leadsTables.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            No leads tables found. Create your first table to get started.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            {leadsTables.map(table => (
              <Chip
                key={table.id}
                label={table.name}
                onClick={() => fetchLeadsFromTable(table.id)}
                color={selectedTable && selectedTable.id === table.id ? 'primary' : 'default'}
                variant={selectedTable && selectedTable.id === table.id ? 'filled' : 'outlined'}
                sx={{ m: 0.5 }}
                onDelete={() => handleDeleteTable(table.id, table.name)}
                deleteIcon={<DeleteIcon />}
              />
            ))}
          </Box>
        )}
      </Paper>

      {/* Leads Table */}
      {selectedTable && (
        <Paper elevation={3} sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {selectedTable.name} - Leads
              {loading && <CircularProgress size={20} sx={{ ml: 2 }} />}
            </Typography>
            <Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => fetchLeadsFromTable(selectedTable.id)}
                sx={{ mr: 1 }}
            >
              Refresh
            </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setAddLeadDialog(true)}
                sx={{ mr: 1 }}
              >
                Add Lead
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<CloudUploadIcon />}
                onClick={() => setUploadLeadsDialog(true)}
                sx={{ mr: 1 }}
              >
                Upload Leads
              </Button>
              {selectedLeads.length > 0 && (
                <>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CallIcon />}
                    onClick={() => setCallMultipleDialog(true)}
                    sx={{ mr: 1 }}
                  >
                    Call Selected ({selectedLeads.length})
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleBatchDelete}
                  >
                    Delete Selected
                  </Button>
                </>
              )}
            </Box>
          </Box>

          {leads.length === 0 ? (
            <Alert severity="info">
              No leads found in this table. Upload some leads to get started.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedLeads.length > 0 && selectedLeads.length < leads.length}
                        checked={leads.length > 0 && selectedLeads.length === leads.length}
                        onChange={toggleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Phone Number</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Call Count</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leads.map(lead => (
                    <TableRow key={lead.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedLeads.includes(lead.id)}
                          onChange={() => toggleLeadSelection(lead.id)}
                        />
                      </TableCell>
                      <TableCell>
                        {editingCell.field === 'phone_number' && editingCell.leadId === lead.id ? (
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                              <CountrySelector
                                value={phoneCountry}
                                onChange={(code) => setPhoneCountry(code)}
                                small
                                sx={{ width: '80px', mr: 1 }}
                              />
                              <TextField
                                value={editingCell.value}
                                onChange={(e) => {
                                  setEditingCell({
                                    ...editingCell,
                                    value: e.target.value
                                  });
                                }}
                                size="small"
                                autoFocus
                                placeholder="Phone number"
                                sx={{ flexGrow: 1 }}
                              />
                              <IconButton 
                                size="small" 
                                color="primary" 
                                onClick={handleEditSave}
                                sx={{ ml: 1 }}
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                color="error" 
                                onClick={handleEditCancel}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        ) : (
                          <Box 
                            onClick={() => handleEditStart(lead, 'phone_number')}
                            sx={{ 
                              cursor: 'pointer', 
                              p: 1, 
                              borderRadius: 1,
                              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' } 
                            }}
                          >
                            {lead.phone_number || ''}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                          {editingCell.leadId === lead.id && editingCell.field === 'first_name' ? (
                            <ClickAwayListener onClickAway={handleEditCancel}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <TextField
                                  value={editingCell.value}
                                  onChange={handleEditChange}
                                  size="small"
                                  autoFocus
                                  variant="outlined"
                                  sx={{ width: 120 }}
                                />
                                <IconButton 
                                  size="small" 
                                  color="primary" 
                                  onClick={handleEditSave}
                                  sx={{ ml: 1 }}
                                >
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                                <IconButton 
                                  size="small" 
                                  color="error" 
                                  onClick={handleEditCancel}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </ClickAwayListener>
                          ) : (
                            <Box 
                              onClick={() => handleEditStart(lead, 'first_name')}
                              sx={{ 
                                cursor: 'pointer', 
                                p: 1, 
                                borderRadius: 1,
                                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' } 
                              }}
                            >
                              {lead.first_name || 'N/A'}
                            </Box>
                          )}
                          
                          {editingCell.leadId === lead.id && editingCell.field === 'last_name' ? (
                            <ClickAwayListener onClickAway={handleEditCancel}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <TextField
                                  value={editingCell.value}
                                  onChange={handleEditChange}
                                  size="small"
                                  autoFocus
                                  variant="outlined"
                                  sx={{ width: 120 }}
                                />
                                <IconButton 
                                  size="small" 
                                  color="primary" 
                                  onClick={handleEditSave}
                                  sx={{ ml: 1 }}
                                >
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                                <IconButton 
                                  size="small" 
                                  color="error" 
                                  onClick={handleEditCancel}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </ClickAwayListener>
                          ) : (
                            <Box 
                              onClick={() => handleEditStart(lead, 'last_name')}
                              sx={{ 
                                cursor: 'pointer', 
                                p: 1, 
                                borderRadius: 1,
                                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' } 
                              }}
                            >
                              {lead.last_name || ''}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {editingCell.leadId === lead.id && editingCell.field === 'email' ? (
                          <ClickAwayListener onClickAway={handleEditCancel}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <TextField
                                value={editingCell.value}
                                onChange={handleEditChange}
                                size="small"
                                autoFocus
                                variant="outlined"
                                sx={{ width: 200 }}
                              />
                              <IconButton 
                                size="small" 
                                color="primary" 
                                onClick={handleEditSave}
                                sx={{ ml: 1 }}
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                color="error" 
                                onClick={handleEditCancel}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </ClickAwayListener>
                        ) : (
                          <Box 
                            onClick={() => handleEditStart(lead, 'email')}
                            sx={{ 
                              cursor: 'pointer', 
                              p: 1, 
                              borderRadius: 1,
                              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' } 
                            }}
                          >
                            {lead.email || 'N/A'}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>{lead.call_count || 0}</TableCell>
                      <TableCell>
                        <Tooltip title="Call Lead">
                          <IconButton
                            color="primary"
                            onClick={() => {
                              setSelectedLeads([lead.id]);
                              setCallLeadDialog(true);
                            }}
                          >
                            <PhoneIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Create Table Dialog */}
      <Dialog open={createTableDialog} onClose={() => setCreateTableDialog(false)}>
        <DialogTitle>Create New Leads Table</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Table Name"
            fullWidth
            value={newTableName}
            onChange={(e) => setNewTableName(e.target.value)}
          />
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            Each leads table will have the following columns:
            <ul>
              <li>Phone Number (required)</li>
              <li>Email (optional)</li>
              <li>First Name (optional)</li>
              <li>Last Name (optional)</li>
            </ul>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateTableDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateTable} variant="contained" color="primary">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Leads Dialog */}
      <Dialog open={uploadLeadsDialog} onClose={() => setUploadLeadsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Upload Leads to {selectedTable?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Upload a CSV or Excel file with leads. The file should have columns for phone numbers, emails and names.
          </Typography>
          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUploadIcon />}
            fullWidth
            sx={{ mt: 2 }}
          >
            Select File (CSV or Excel)
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              hidden
              onChange={handleFileSelect}
            />
          </Button>
          {tempFile && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Selected file: {tempFile.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadLeadsDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Add Lead Dialog */}
      <Dialog open={addLeadDialog} onClose={() => setAddLeadDialog(false)}>
        <DialogTitle>Add New Lead</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Phone Number (with country code)
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CountrySelector 
                value={phoneCountry}
                onChange={(code) => setPhoneCountry(code)}
                sx={{ width: '100px', mr: 1 }}
              />
              <TextField
                label="Phone Number"
                fullWidth
              value={newLead.phone_number}
                onChange={(e) => setNewLead({ ...newLead, phone_number: e.target.value })}
                placeholder="Enter phone number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1, mb: 2, display: 'block' }}>
              Country code will be added automatically. No need to type + or country code.
            </Typography>
            
            <TextField
              margin="dense"
              label="Email"
              fullWidth
              value={newLead.email}
              onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="First Name"
              fullWidth
              value={newLead.first_name}
              onChange={(e) => setNewLead({ ...newLead, first_name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Last Name"
              fullWidth
              value={newLead.last_name}
              onChange={(e) => setNewLead({ ...newLead, last_name: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddLeadDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddLead} 
            variant="contained"
            color="primary"
            disabled={!newLead.phone_number}
          >
            Add Lead
          </Button>
        </DialogActions>
      </Dialog>

      {/* Column Mapping Dialog - with integrated preview and country code selection */}
      <Dialog open={columnMappingDialog} onClose={() => setColumnMappingDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Map Columns and Configure Import</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Please verify the column mapping for your file and select a default country code for phone numbers.
          </Typography>
          
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel id="phone-column-label">Phone Number Column (Required)</InputLabel>
                <Select
                  labelId="phone-column-label"
                  value={columnMapping['phone_number'] || ''}
                  onChange={(e) => setColumnMapping({...columnMapping, 'phone_number': e.target.value})}
                  label="Phone Number Column (Required)"
                >
                  <MenuItem value=""><em>None (Skip)</em></MenuItem>
                  {fileColumns.map(column => (
                    <MenuItem key={column} value={column}>{column}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel id="email-column-label">Email Column</InputLabel>
                <Select
                  labelId="email-column-label"
                  value={columnMapping['email'] || ''}
                  onChange={(e) => setColumnMapping({...columnMapping, 'email': e.target.value})}
                  label="Email Column"
                >
                  <MenuItem value=""><em>None (Skip)</em></MenuItem>
                  {fileColumns.map(column => (
                    <MenuItem key={column} value={column}>{column}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel id="firstname-column-label">First Name Column</InputLabel>
                <Select
                  labelId="firstname-column-label"
                  value={columnMapping['first_name'] || ''}
                  onChange={(e) => setColumnMapping({...columnMapping, 'first_name': e.target.value})}
                  label="First Name Column"
                >
                  <MenuItem value=""><em>None (Skip)</em></MenuItem>
                  {fileColumns.map(column => (
                    <MenuItem key={column} value={column}>{column}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel id="lastname-column-label">Last Name Column</InputLabel>
                <Select
                  labelId="lastname-column-label"
                  value={columnMapping['last_name'] || ''}
                  onChange={(e) => setColumnMapping({...columnMapping, 'last_name': e.target.value})}
                  label="Last Name Column"
                >
                  <MenuItem value=""><em>None (Skip)</em></MenuItem>
                  {fileColumns.map(column => (
                    <MenuItem key={column} value={column}>{column}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              Phone Number Format Settings
            </Typography>
            
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" gutterBottom>Default Country Code</Typography>
                <CountrySelector
                    value={phoneCountry}
                  onChange={(code) => setPhoneCountry(code)}
                  sx={{ width: '100%' }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={8}>
                <FormControl component="fieldset" sx={{ ml: 2 }}>
                  <Typography variant="body2" gutterBottom>Number Format Rules:</Typography>
                  <Typography variant="caption" display="block">
                    • Leading zeros will be removed and replaced with country code
                  </Typography>
                  <Typography variant="caption" display="block">
                    • Numbers starting with + will be preserved as is
                  </Typography>
                  <Typography variant="caption" display="block">
                    • Numbers without country code will have the default country code applied
                  </Typography>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            At minimum, you must map a column to "Phone Number" to import leads.
          </Alert>
          
          <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
            Data Preview
          </Typography>
          
          {previewData.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Phone Number</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>First Name</TableCell>
                    <TableCell>Last Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {reviewNumbersMode && columnMapping['phone_number'] ? (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CountrySelector
                              value={phoneCountry}
                              onChange={(code) => setPhoneCountry(code)}
                              small
                              sx={{ width: '80px', mr: 1 }}
                            />
                            <TextField
                              value={phoneNumberOverrides[index]?.override || row[columnMapping['phone_number']]}
                              onChange={(e) => handlePhoneNumberOverride(index, row[columnMapping['phone_number']], e.target.value)}
                              size="small"
                              placeholder="Phone number"
                              fullWidth
                            />
                          </Box>
                        ) : (
                          columnMapping['phone_number'] ? row[columnMapping['phone_number']] : 'N/A'
                        )}
                      </TableCell>
                      <TableCell>
                        {columnMapping['email'] ? row[columnMapping['email']] : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {columnMapping['first_name'] ? row[columnMapping['first_name']] : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {columnMapping['last_name'] ? row[columnMapping['last_name']] : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary">
              No preview data available. Please make sure your file has valid data rows.
            </Typography>
          )}
          
          {reviewNumbersMode && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Review and edit phone numbers if needed. You can manually adjust any number that requires special formatting or a different country code.
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            The preview shows up to {previewData.length} rows from your file. The import will process all rows.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setColumnMappingDialog(false);
            setReviewNumbersMode(false);
            setTempFile(null);
            setCsvFile(null);
            setColumnMapping({});
            resetOverrides();
            setPreviewStep(false);
          }}>
            Cancel
          </Button>
          
          {reviewNumbersMode ? (
            <>
              <Button 
                onClick={() => setReviewNumbersMode(false)}
                variant="outlined"
              >
                Back to Mapping
              </Button>
              <Button
                onClick={handleUploadLeadsWithMapping}
                variant="contained"
                color="primary"
                disabled={!tempFile || !columnMapping['phone_number']}
              >
                Import Leads
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={proceedToReviewNumbers}
                variant="contained"
                color="primary"
                disabled={!tempFile || !columnMapping['phone_number']}
              >
                Review Numbers
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Call Lead Dialog */}
      <Dialog open={callLeadDialog} onClose={() => setCallLeadDialog(false)}>
        <DialogTitle>Call Lead</DialogTitle>
        <DialogContent>
          {selectedLeads.length > 0 && (
            <>
              <Typography variant="body1" gutterBottom>
                Lead: {(() => {
                  const lead = leads.find(l => l.id === selectedLeads[0]);
                  return lead ? (
                    <>
                      {lead.first_name ? `${lead.first_name} ${lead.last_name || ''}` : 'N/A'} 
                      <Typography component="span" color="primary">
                        ({lead.phone_number})
                      </Typography>
                    </>
                  ) : 'Unknown';
                })()}
              </Typography>
              
              <FormControl fullWidth margin="normal">
                <InputLabel id="agent-select-label">Select Agent</InputLabel>
                <Select
                  labelId="agent-select-label"
                  value={selectedAgent}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                  label="Select Agent"
                >
                  {agents.length === 0 ? (
                    <MenuItem disabled>No outbound agents available</MenuItem>
                  ) : (
                    agents.map(agent => (
                      <MenuItem key={agent.id} value={agent.name}>
                        {agent.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              
              {callStatus === 'calling' && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography>Initiating call...</Typography>
                </Box>
              )}
              
              {callStatus === 'error' && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Failed to initiate call. Please try again.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCallLeadDialog(false)}>Cancel</Button>
          <Button
            onClick={() => {
              const lead = leads.find(l => l.id === selectedLeads[0]);
              if (lead) handleCallLead(lead);
            }}
            variant="contained"
            color="primary"
            disabled={selectedLeads.length === 0 || !selectedAgent || callStatus === 'calling'}
            startIcon={<CallIcon />}
          >
            Call Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Call Multiple Leads Dialog */}
      <Dialog open={callMultipleDialog} onClose={() => setCallMultipleDialog(false)}>
        <DialogTitle>Call Multiple Leads</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            You are about to call {selectedLeads.length} leads.
          </Typography>
          
          <FormControl fullWidth margin="normal">
            <InputLabel id="agent-select-multi-label">Select Agent</InputLabel>
            <Select
              labelId="agent-select-multi-label"
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
              label="Select Agent"
            >
              {agents.length === 0 ? (
                <MenuItem disabled>No outbound agents available</MenuItem>
              ) : (
                agents.map(agent => (
                  <MenuItem key={agent.id} value={agent.name}>
                    {agent.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            Calls will be placed one after another. You can cancel at any time.
          </Alert>
          
          {callStatus === 'calling' && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              <Typography>Processing calls...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCallMultipleDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCallMultipleLeads}
            variant="contained"
            color="primary"
            disabled={selectedLeads.length === 0 || !selectedAgent || callStatus === 'calling'}
            startIcon={<CallIcon />}
          >
            Start Calling
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Client Dialog */}
      <Dialog open={createClientDialog} onClose={() => setCreateClientDialog(false)}>
        <DialogTitle>Create New Client</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Client Name"
            fullWidth
            value={newClient.name}
            onChange={(e) => setNewClient({...newClient, name: e.target.value})}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            label="Client Email"
            type="email"
            fullWidth
            value={newClient.email}
            onChange={(e) => setNewClient({...newClient, email: e.target.value})}
            sx={{ mb: 2 }}
          />
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            This will create a new client organization and an admin user for that client.
            A secure password will be automatically generated and sent to the client's email.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateClientDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateClient} variant="contained" color="primary">
            Create Client
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Leads; 